import { Injectable } from '@angular/core';
import { OrderCommandService } from 'src/app/shared/data-service/order-command.service';
import { Analysis, AnalysisType } from 'projects/dop-ordering/src/public-api';
import { CurrentOrder } from 'src/app/shared/models/current-order.model';
import { Sample } from 'src/app/shared/models/sample.model';
import { ParametersService } from './parameters.service';
import { InvoiceableItemType } from 'src/app/shared/models/specification/analyses-response.model';
import { SpecificationHelper } from './specification-helper.service';
import { SpecificationViewModel } from 'src/app/shared/models/specification/specification-view-model.model';
import { SettingsService } from 'src/app/shared/data-service/settings.service';
import { SettingsKey } from 'src/app/core/store/settings-config.keys';

@Injectable({
  providedIn: 'root'
})
export class RemoveAnalysisService {

  constructor(
    private orderCommandService: OrderCommandService,
    private parametersService: ParametersService,
    private specificationHelper: SpecificationHelper,
    private settingsService: SettingsService) { }

  async removeAnalysis(analysis: Analysis[], currentOrder: CurrentOrder, sampleCode: string) {
    for (const analyses of analysis) {
      switch (analyses.analysisType) {
        case AnalysisType.CustomerPackage:
          await this.removeItemsForCustomerPackage(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.FullSubgroup: case AnalysisType.SubgroupWithTest: case AnalysisType.SubgroupWithPackage:
          await this.removeItemsForSubGroup(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.FreeQLPackage:
          await this.removeItemsForPackage(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.FreeQLTest:
          await this.removeItemsForTest(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.MonographTest:
          await this.removeMonographTest(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.MonographPackage:
          await this.removeMonographPackage(sampleCode, currentOrder, analyses);
          break;

        case AnalysisType.SalesCatalogueTest:
          if (!analyses.testBundleCode) {
            await this.removeItemsForSCTest(sampleCode, currentOrder, analyses);
          } else {
            await this.removeItemsForTestBundleTest(sampleCode, currentOrder, analyses);
          }
          break;

        case AnalysisType.SalesCataloguePackage:
          await this.removeItemsForSCPackage(sampleCode, currentOrder, analyses);

          break;
      }
    }
  }

  private removeItemsForCustomerPackage(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForCustomerPackage(sampleCode, currentOrder.quotation.code,
        analysis.eurodatKey).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private removeItemsForSubGroup(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForSubgroupQl(sampleCode, currentOrder.quotation.code,
        analysis.subGroupKey, analysis.eurodatKey).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private removeItemsForPackage(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForPackageQl(sampleCode, currentOrder.quotation.code,
        analysis.eurodatKey).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private removeItemsForTest(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForTestQl(sampleCode, currentOrder.quotation.code,
        analysis.eurodatKey).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private async removeMonographTest(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    const monographSalesCatalogCode = await this.settingsService.getSettingValue(SettingsKey.ConfigureMonograph);
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForSCTest(sampleCode, monographSalesCatalogCode, analysis.testCode)
        .subscribe(async data => {
          await this.cleaupSampleDetailsAfterMonographRemoval(sampleCode, currentOrder, analysis);
          resolve(data);
        });
      });
    }

  private removeItemsForSCPackage(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForSCPackage(sampleCode, analysis.salesCatalogCode,
        analysis.packageCode).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private async removeMonographPackage(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    const monographSalesCatalogCode = await this.settingsService.getSettingValue(SettingsKey.ConfigureMonograph);
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForSCPackage(sampleCode, monographSalesCatalogCode, analysis.packageCode)
        .subscribe(async data => {
          await this.cleaupSampleDetailsAfterMonographRemoval(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private removeItemsForSCTest(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForSCTest(sampleCode, analysis.salesCatalogCode,
        analysis.testCode).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private removeItemsForTestBundleTest(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItemForTestBundleTests(sampleCode, analysis.salesCatalogCode,
        analysis.testBundleCode, analysis.testCode).subscribe(async data => {
          await this.cleanupSampleDetails(sampleCode, currentOrder, analysis);
          resolve(data);
        });
    });
  }

  private async cleanupSampleDetails(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis) {
    const sample = currentOrder.samples.filter(x => x.sampleCode === sampleCode).shift();
    this.filterSamplePlanningLineResponse(sample, analysis);
    sample.analyses = sample.analyses.filter(x => (x.eurodatKey && x.eurodatKey !== analysis.eurodatKey) ||
    ((!x.eurodatKey && x.salesCatalogCode !== analysis.salesCatalogCode) || (x.testBundleCode !== analysis.testBundleCode) ||
    (x.analysisType === AnalysisType.SalesCatalogueTest && x.testCode !== analysis.testCode) ||
    (x.analysisType === AnalysisType.SalesCataloguePackage && x.packageCode !== analysis.packageCode)));
    await this.resetParametersDisplay(sample, currentOrder);
    this.cleanupSampleSpecificationResponses(sample);
    this.reloadSpecifications(sample, currentOrder);
  }

  private async cleaupSampleDetailsAfterMonographRemoval(sampleCode: string, currentOrder: CurrentOrder, analysis: Analysis) {
    const sample = currentOrder.samples.filter(x => x.sampleCode === sampleCode).shift();
    this.filterSamplePlanningLineResponse(sample, analysis);
    sample.analyses = sample.analyses.filter(x => x.testCode !== analysis.testCode
      || x.packageCode !== analysis.packageCode
      || x.monographCode !== analysis.monographCode);
  }

  private reloadSpecifications(sample: Sample, currentOrder: CurrentOrder) {
    const response = this.specificationHelper
      .convertToSpecificationViewModel(sample, currentOrder.units);
    sample.specifications = ([] as SpecificationViewModel[]).concat(...response.map((p: SpecificationViewModel) => {
      p.parameters = p.parameters.filter(t => t.standardSpecification.hasValue || t.servingSpecification?.hasValue
        || t.dailyValueSpecification?.hasValue);
      return p;
    })).filter(t => t.parameters.length > 0);

    sample.isSpecificationsAdded = sample.specifications.length > 0;

    const index = currentOrder.samples.findIndex(x => x.sampleCode === sample.sampleCode);
    currentOrder.samples[index] = { ...sample };
  }

  private filterSamplePlanningLineResponse(sample: Sample, analysis: Analysis) {
    switch (analysis.analysisType) {
      case AnalysisType.CustomerPackage:
        sample.planningLines = sample.planningLines.filter(t => !(t.groupCode === analysis.eurodatKey
          && t.invoiceableItemType === InvoiceableItemType.CustomerPackage));
        break;

      case AnalysisType.FullSubgroup: case AnalysisType.SubgroupWithTest: case AnalysisType.SubgroupWithPackage:
        if (analysis.eurodatKey) {
          sample.planningLines = sample.planningLines.filter(t =>
            !(t.groupCode === analysis.subGroupKey && t.quotationLineCode === analysis.eurodatKey &&
              (t.invoiceableItemType === InvoiceableItemType.PackageQuotationLine ||
                t.invoiceableItemType === InvoiceableItemType.TestQuotationLine)));
        } else {
          sample.planningLines = sample.planningLines.filter(t =>
            !(t.groupCode === analysis.subGroupKey &&
              (t.invoiceableItemType === InvoiceableItemType.PackageQuotationLine ||
                t.invoiceableItemType === InvoiceableItemType.TestQuotationLine)));
        }
        break;

      case AnalysisType.FreeQLPackage:
        sample.planningLines = sample.planningLines.filter(t => !(t.packageCode === analysis.packageCode
          && t.invoiceableItemType === InvoiceableItemType.Package && t.quotationLineCode));
        break;

      case AnalysisType.FreeQLTest:
        sample.planningLines = sample.planningLines.filter(t => !(t.testCode === analysis.testCode
          && t.invoiceableItemType === InvoiceableItemType.TestQuotationLine && t.quotationLineCode));
        break;

      case AnalysisType.SalesCatalogueTest:
        sample.planningLines = sample.planningLines.filter(t => !(t.testCode === analysis.testCode
          && t.invoiceableItemType === InvoiceableItemType.Test && !t.quotationLineCode));
        break;

      case AnalysisType.SalesCataloguePackage:
        sample.planningLines = sample.planningLines.filter(t => !(t.packageCode === analysis.packageCode
          && t.invoiceableItemType === InvoiceableItemType.Package && !t.quotationLineCode));
        break;

      case AnalysisType.SalesCatalogueTestBundle:
        sample.planningLines = sample.planningLines.filter(t => !(t.packageCode === analysis.testBundleCode
          && t.testCode === analysis.testCode
          && t.invoiceableItemType === InvoiceableItemType.Test && !t.quotationLineCode));
        break;

      case AnalysisType.MonographTest:
        sample.planningLines = sample.planningLines.filter(t => !(t.testCode === analysis.testCode
          && t.invoiceableItemType === InvoiceableItemType.Test
          && !!t.monographCode));
        break;

      case AnalysisType.MonographPackage:
        sample.planningLines = sample.planningLines.filter(t => !(t.packageCode === analysis.packageCode
          && t.invoiceableItemType === InvoiceableItemType.Package
          && !!t.monographCode));
        break;
    }
  }

  cleanupSampleSpecificationResponses(sample: Sample) {
    const specifications = JSON.parse(JSON.stringify(sample.specifications));
    for (let i = 0; i < specifications.length; i++) {
      const element = specifications[i];
      element.parameters.forEach((p: any) => {
        let planningLineIds: string[] = [];
        p.analyses.forEach((analysis: any) => {
          planningLineIds = [...planningLineIds.concat(sample.planningLines
            .filter(a => a.testCode === analysis.code).map(b => b.id.toString()))];
        });
        if (planningLineIds.length === 0) {
          sample.specifications[i].parameters = sample.specifications[i].parameters
            .filter(t => t.parameterCode !== p.parameterCode);
          sample.specificationResponses = sample.specificationResponses.filter(t => t.parameterCode !== p.parameterCode);
        }
      });
    }
  }

  removeAllAnalysis(sample: Sample): Promise<any> {
    return new Promise((resolve) => {
      this.orderCommandService.removeSampleInvoiceableItems(sample.sampleCode).subscribe(data => {
        sample.specifications.length = 0;
        sample.planningLines.length = 0;
        sample.specificationResponses.length = 0;
        sample.isSpecificationsAdded = false;
        sample.analyses.length = 0;
        resolve(data);
      });
    });
  }

  async resetParametersDisplay(sample: Sample, currentOrder: CurrentOrder) {
    await this.parametersService.getParameterDetails(sample, currentOrder);
  }


  isTestPreparationTestForOthers(analyses: Analysis, sample: Sample) {
    let isTestaPreparationTestForOthers = false;
    const otherAnalysis
      = sample.analyses.filter(c => c.preparationTestCodes && c.preparationTestCodes.length > 0
        && ((c.eurodatKey && c.eurodatKey !== analyses.eurodatKey) || c.testCode !== analyses.testCode) && !c.isMandatory);
    let prepTestCodesOfOtherAnalysis = [];
    otherAnalysis.forEach(a => {
      prepTestCodesOfOtherAnalysis.push(...a.preparationTestCodes?.map(x => x.trim()));
    });
    // filter analysis which are already there as part of prep test
    prepTestCodesOfOtherAnalysis = prepTestCodesOfOtherAnalysis
      .filter(ptCode => !(sample.analyses?.filter(x => x.isMandatory).map(c => c.testCode).indexOf(ptCode) !== -1));
    if (prepTestCodesOfOtherAnalysis.some(c => c === analyses.testCode && !analyses.isMandatory)) {
      analyses.isMandatory = true;
      isTestaPreparationTestForOthers = true;
      return true;
    }
    return isTestaPreparationTestForOthers;
  }

  getPreparationTestsToRemove(analyses: Analysis, sample: Sample) {
    const preparationTestsToRemove: Analysis[] = [];
    const selectedPrepTests = sample.analyses.filter(a => a.isMandatory);
    analyses.preparationTestCodes = analyses.preparationTestCodes?.map(p => p.trim());
    selectedPrepTests.forEach(spt => {
      if (analyses.preparationTestCodes &&
        analyses.preparationTestCodes.indexOf(spt.testCode) !== -1 && !this.isPrepTestForOther(spt.testCode, analyses, sample)) {
        preparationTestsToRemove.push(spt);
      }
    });
    return preparationTestsToRemove;
  }

  private isPrepTestForOther(prepTestCode: string, selectedAnalysis: Analysis, sample: Sample) {
    let isNotMrtOfOther = false;
    sample.analyses.forEach(t => {
      t.preparationTestCodes = t.preparationTestCodes?.map(p => p.trim());
      if (t.eurodatKey !== selectedAnalysis.eurodatKey && t.preparationTestCodes &&
        t.preparationTestCodes.indexOf(prepTestCode) !== -1) {
        isNotMrtOfOther = true;
        return true;
      }
    });
    return isNotMrtOfOther;
  }
}
